import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { searchFoodByBarcode, searchFoodByName } from '../../services/nutritionService';
import { addNutritionLog, getNutritionLogs } from '../../services/firestoreService';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

export default function NutritionTracker() {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [barcode, setBarcode] = useState('');
  const [logs, setLogs] = useState([]);
  const [searching, setSearching] = useState(false);
  const [tab, setTab] = useState('log');
  const todayLogs = logs.filter(l => l.date === format(new Date(), 'yyyy-MM-dd'));
  const todayTotals = todayLogs.reduce((acc, l) => ({
    calories: acc.calories + (l.calories || 0),
    protein: acc.protein + (l.protein || 0),
    carbs: acc.carbs + (l.carbs || 0),
    fat: acc.fat + (l.fat || 0),
  }), { calories: 0, protein: 0, carbs: 0, fat: 0 });

  useEffect(() => {
    if (user) getNutritionLogs(user.uid).then(setLogs);
  }, [user]);

  const handleSearch = async () => {
    if (!search) return;
    setSearching(true);
    const data = await searchFoodByName(search);
    setResults(data);
    setSearching(false);
  };

  const handleBarcodeSearch = async () => {
    if (!barcode) return;
    setSearching(true);
    const data = await searchFoodByBarcode(barcode);
    if (data) setResults([data]);
    else toast.error('Product not found');
    setSearching(false);
  };

  const logFood = async (food, servings = 1) => {
    try {
      await addNutritionLog(user.uid, {
        ...food,
        calories: Math.round(food.calories * servings),
        protein: Math.round(food.protein * servings * 10) / 10,
        carbs: Math.round(food.carbs * servings * 10) / 10,
        fat: Math.round(food.fat * servings * 10) / 10,
        servings,
        date: format(new Date(), 'yyyy-MM-dd'),
        meal: 'General',
      });
      toast.success(`${food.name} logged!`);
      getNutritionLogs(user.uid).then(setLogs);
    } catch {
      toast.error('Failed to log food');
    }
  };

  const macroBar = (label, value, max, color) => (
    <div style={{ marginBottom: '0.75rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
        <span style={{ color: theme.textSecondary, fontSize: '0.8rem' }}>{label}</span>
        <span style={{ color: theme.text, fontSize: '0.8rem', fontWeight: 600 }}>{Math.round(value)}g</span>
      </div>
      <div style={{ height: '8px', background: theme.border, borderRadius: '4px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${Math.min(100, (value / max) * 100)}%`, background: color, borderRadius: '4px', transition: 'width 0.8s ease' }} />
      </div>
    </div>
  );

  const tabStyle = (t) => ({
    padding: '0.6rem 1.25rem', border: 'none', cursor: 'pointer',
    background: tab === t ? theme.accent : 'transparent',
    color: tab === t ? '#fff' : theme.textSecondary,
    borderRadius: '8px', fontFamily: "'Exo 2', sans-serif",
    fontWeight: tab === t ? 600 : 400, transition: 'all 0.2s ease',
  });

  return (
    <div style={{ padding: '2rem', maxWidth: '1000px' }} className="animate-fade">
      <h1 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '1.8rem', fontWeight: 700, color: theme.text, marginBottom: '2rem' }}>
        🥗 Nutrition Tracker
      </h1>

      {/* Today Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        {[
          { label: "Today's Calories", value: Math.round(todayTotals.calories), unit: 'kcal', color: theme.accent },
          { label: 'Protein', value: Math.round(todayTotals.protein), unit: 'g', color: theme.success },
          { label: 'Carbs', value: Math.round(todayTotals.carbs), unit: 'g', color: theme.warning },
          { label: 'Fat', value: Math.round(todayTotals.fat), unit: 'g', color: theme.accentSecondary },
        ].map((s, i) => (
          <div key={i} className="card" style={{ textAlign: 'center' }}>
            <div style={{ color: theme.textSecondary, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
            <div style={{ fontSize: '1.6rem', fontWeight: 700, fontFamily: "'Rajdhani', sans-serif", color: s.color }}>
              {s.value}<span style={{ fontSize: '0.8rem', fontWeight: 400 }}> {s.unit}</span>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
        <button style={tabStyle('log')} onClick={() => setTab('log')}>🔍 Log Food</button>
        <button style={tabStyle('barcode')} onClick={() => setTab('barcode')}>📷 Barcode</button>
        <button style={tabStyle('history')} onClick={() => setTab('history')}>📋 Today's Log</button>
      </div>

      {tab === 'log' && (
        <div>
          <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem' }}>
            <input value={search} onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder="Search food (e.g. chicken breast, banana, pizza...)"
              style={{ flex: 1 }} />
            <button className="btn btn-primary" onClick={handleSearch} disabled={searching}>
              {searching ? '...' : '🔍 Search'}
            </button>
          </div>

          {results.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {results.slice(0, 10).map((food, i) => (
                <div key={i} className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    {food.image && <img src={food.image} alt="" style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '8px' }} />}
                    <div>
                      <div style={{ fontWeight: 600, color: theme.text }}>{food.name}</div>
                      {food.brand && <div style={{ color: theme.textSecondary, fontSize: '0.8rem' }}>{food.brand}</div>}
                      <div style={{ color: theme.textSecondary, fontSize: '0.75rem', marginTop: '0.25rem' }}>
                        {Math.round(food.calories)} kcal · {Math.round(food.protein)}g protein · {Math.round(food.carbs)}g carbs · {Math.round(food.fat)}g fat
                        <span style={{ marginLeft: '0.5rem' }}>per {food.servingSize}</span>
                      </div>
                    </div>
                  </div>
                  <button className="btn btn-primary" style={{ padding: '0.5rem 1rem', fontSize: '0.85rem' }}
                    onClick={() => logFood(food)}>
                    + Log
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'barcode' && (
        <div className="card" style={{ textAlign: 'center', maxWidth: '480px' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📷</div>
          <h3 style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, color: theme.text, marginBottom: '0.5rem' }}>Barcode Scanner</h3>
          <p style={{ color: theme.textSecondary, fontSize: '0.85rem', marginBottom: '1.5rem' }}>
            Enter the barcode number from any food package. Works with 600,000+ products.
          </p>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <input value={barcode} onChange={e => setBarcode(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleBarcodeSearch()}
              placeholder="Enter barcode (e.g. 012345678901)" />
            <button className="btn btn-primary" onClick={handleBarcodeSearch} disabled={searching}>
              {searching ? '...' : 'Look Up'}
            </button>
          </div>
          {results.length > 0 && results[0] && (
            <div className="card" style={{ marginTop: '1.5rem', textAlign: 'left' }}>
              {results[0].image && <img src={results[0].image} alt="" style={{ width: '100%', maxHeight: '160px', objectFit: 'contain', marginBottom: '1rem' }} />}
              <div style={{ fontWeight: 600, color: theme.text, marginBottom: '0.25rem' }}>{results[0].name}</div>
              {results[0].brand && <div style={{ color: theme.textSecondary, fontSize: '0.85rem', marginBottom: '0.75rem' }}>{results[0].brand}</div>}
              {macroBar('Calories', results[0].calories, 2000, theme.accent)}
              {macroBar('Protein', results[0].protein, 150, theme.success)}
              {macroBar('Carbs', results[0].carbs, 250, theme.warning)}
              {macroBar('Fat', results[0].fat, 65, theme.accentSecondary)}
              <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: '0.5rem' }}
                onClick={() => logFood(results[0])}>
                + Log This Food
              </button>
            </div>
          )}
        </div>
      )}

      {tab === 'history' && (
        <div>
          <h3 style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, color: theme.text, marginBottom: '1rem' }}>
            Today's Food Log — {format(new Date(), 'MMMM d, yyyy')}
          </h3>
          {todayLogs.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '3rem', color: theme.textSecondary }}>
              Nothing logged today yet. Start searching for food!
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {todayLogs.map((log, i) => (
                <div key={log.id || i} className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 600, color: theme.text }}>{log.name}</div>
                    <div style={{ color: theme.textSecondary, fontSize: '0.8rem' }}>
                      {log.calories} kcal · P: {log.protein}g · C: {log.carbs}g · F: {log.fat}g
                    </div>
                  </div>
                  <div style={{ color: theme.accent, fontWeight: 700 }}>{log.calories} kcal</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
